import { useEffect, useMemo, useRef, useState } from 'react';
import { BOT_CATALOGUE, type BotLevel } from '../bots/index.js';
import type { GameResult } from '../engine/index.js';
import {
  DEFAULT_RULES,
  validateConfig,
  type ConfigProblem,
  type RuleConfig,
} from '../shared/rules.js';
import { APP_VERSION } from '../shared/version.js';
import type { RoomState } from '../shared/protocol.js';
import { Board } from './game/Board.js';
import { describeEvent, describePublicEvent } from './game/log.js';
import { modelFromState, modelFromView } from './game/model.js';
import { RulesPanel } from './game/RulesPanel.js';
import { defaultSetup, useLocalGame, type LocalGameSetup } from './game/useLocalGame.js';
import { useI18n, useT, type Translate } from './i18n/index.js';
import { Chat, Lobby } from './net/Lobby.js';
import { useOnline } from './net/useOnline.js';
import { SettingsPanel } from './settings/SettingsPanel.js';
import { FamilyHub } from './family/FamilyHub.js';
import { VoicePanel, VoiceProvider } from './family/Voice.js';
import { useSettings, type Settings } from './settings/useSettings.js';
import { Shell, StatusChip, type MenuSpec, type Mode } from './Shell.js';
import { PongBoard } from './pong/PongBoard.js';
import { CodenamesEntry, CodenamesRoom } from './codenames/CodenamesGame.js';
import { GameRoomShell } from './family/GameRoomShell.js';

type SetSetting = <K extends keyof Settings>(key: K, value: Settings[K]) => void;

interface ModeProps {
  mode: Mode;
  setMode: (mode: Mode) => void;
  settings: Settings;
  set: SetSetting;
}

export function App() {
  const [route, setRoute] = useState(() => (typeof window !== 'undefined' ? window.location.hash : ''));
  useEffect(() => {
    const onHashChange = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const content = route.startsWith('#/family/game') ? <FamilyGameRoute /> : route.startsWith('#/r/') ? <FamilyHub {...(roomCodeFromHash() ? { roomCode: roomCodeFromHash()! } : {})} /> : <FamilyHub />;
  return <VoiceProvider>{content}</VoiceProvider>;
  /* istanbul ignore next */
  /* legacy route branches retained below for type-only documentation */
  /*
  if (typeof window !== 'undefined' && window.location.hash.startsWith('#/family')) {
    if (window.location.hash.startsWith('#/family/game')) return <FamilyGameRoute />;
    return <FamilyHub />;
  }
  if (typeof window !== 'undefined' && window.location.hash.startsWith('#/r/')) {
    return <FamilyHub roomCode={roomCodeFromHash() ?? undefined} />;
  }
  // Invite links use the hash so they work with the static web server. Opening
  // one must take the player straight to the online lobby instead of leaving
  // them on the local-game landing screen.
  const [mode, setMode] = useState<Mode>(() => (roomCodeFromHash() === null ? 'local' : 'online'));
  const { settings, set } = useSettings();
  const shared = { mode, setMode, settings, set };

  return <FamilyHub />;
  */
}


function FamilyGameRoute() {
  const { settings, set } = useSettings();
  return <OnlineGame mode="online" setMode={() => {}} settings={settings} set={set} />;
}

function roomCodeFromHash(): string | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.hash.match(/(?:#\/r\/|room=)([A-Za-z0-9]{5})/);
  return match?.[1]?.toUpperCase() ?? null;
}

// --- on this device ---------------------------------------------------------

export function LocalGame({ mode, setMode, settings, set }: ModeProps) {
  const t = useT();
  // A player at the table, not a chair in the setup panel — different words on
  // purpose, so the two never collide in the UI.
  const localSeatName = (seat: number): string => t('player.seat', { n: seat + 1 });
  const game = useLocalGame(defaultSetup(), {
    botDelayMs: settings.botDelayMs,
    holdForThrowIn: settings.holdForThrowIn,
  });
  const [draft, setDraft] = useState<LocalGameSetup>(game.setup);

  const verdict = useMemo(() => validateConfig(draft.config, draft.players), [draft]);
  const model = useMemo(
    // `t` is in the deps because seat names are translated: switching language
    // has to redraw the board, not just the chrome around it.
    () => modelFromState(game.state, { seatName: localSeatName, isBot: game.isBot }),
    [game.state, game.isBot, t],
  );

  const log = useMemo(() => {
    const lines: string[] = [];
    for (const e of game.events) {
      const line = describeEvent(e, t, localSeatName);
      if (line !== null) lines.push(line);
    }
    return lines.slice(-14).reverse();
  }, [game.events, t]);


  const menus: MenuSpec[] = [
    {
      id: 'settings',
      label: t('setup.title'),
      body: (
        <>
          <div className="panel__row">
            <label>
              {t('field.players')}
              <select
                value={draft.players}
                onChange={(e) => {
                  const players = Number(e.target.value);
                  const bots = Array.from(
                    { length: players },
                    (_, i) => draft.bots[i] ?? (i === 0 ? null : 2),
                  );
                  setDraft({ ...draft, players, bots });
                }}
              >
                {[2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>

            <label>
              {t('field.deck')}
              <select
                value={draft.config.deckSize}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    config: {
                      ...draft.config,
                      deckSize: Number(e.target.value) as RuleConfig['deckSize'],
                    },
                  })
                }
              >
                <option value={36}>{t('deck.36')}</option>
                <option value={52}>{t('deck.52')}</option>
              </select>
            </label>

            <label>
              {t('field.seed')}
              <input
                value={draft.seed}
                onChange={(e) => setDraft({ ...draft, seed: e.target.value })}
                size={10}
                title={t('field.seedHint')}
              />
            </label>
          </div>

          <div className="panel__row panel__row--seats">
            {Array.from({ length: draft.players }, (_, seat) => (
              <label key={seat}>
                {t('field.seat', { n: seat + 1 })}
                <select
                  value={draft.bots[seat] ?? 'human'}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      bots: draft.bots.map((b, i) =>
                        i === seat
                          ? e.target.value === 'human'
                            ? null
                            : (Number(e.target.value) as BotLevel)
                          : b,
                      ),
                    })
                  }
                >
                  <option value="human">{t('seat.human')}</option>
                  {BOT_CATALOGUE.map((bot) => (
                    <option key={bot.level} value={bot.level} disabled={!bot.available}>
                      {t(bot.nameKey)}
                      {bot.available ? '' : t('seat.soon')}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <p className="hint">{describeBots(draft.bots.slice(0, draft.players), t)}</p>

          <div className="panel__row">
            <button
              type="button"
              className="btn btn--primary"
              disabled={verdict.errors.length > 0}
              onClick={() => game.restart(draft)}
            >
              {t('action.newGame')}
            </button>
            <button type="button" className="btn" onClick={game.undo} disabled={!game.canUndo}>
              {t('action.undo')}
            </button>
          </div>

          <hr className="menu__rule" />
          <SettingsPanel settings={settings} set={set} />
        </>
      ),
    },
    {
      id: 'rules',
      label: t('rules.title'),
      body: <RulesPanel config={draft.config} onChange={(config) => setDraft({ ...draft, config })} />,
    },
  ];

  return (
    <Shell
      mode={mode}
      setMode={setMode}
      settings={settings}
      set={set}
      menus={menus}
      status={<StatusChip>{t('mode.local')}</StatusChip>}
    >
      <VoicePanel />
      {/* Rule problems sit in the open, not inside the drawer that caused them:
          a warning nobody can see while the table is on screen is no warning. */}
      {verdict.errors.length > 0 ? (
        <p className="problem problem--error sheet">
          {verdict.errors.map((p) => explain(p, t)).join(' ')}
        </p>
      ) : null}
      {verdict.warnings.length > 0 ? (
        <p className="problem problem--warn sheet">
          {verdict.warnings.map((p) => explain(p, t)).join(' ')}
        </p>
      ) : null}

      <div className="stage">
        {game.state.result ? (
          <ResultBanner result={game.state.result} seatName={localSeatName} />
        ) : null}
        <Board
          model={model}
          play={game.play}
          awaitingThrowIn={settings.holdForThrowIn && game.pendingThrowIn !== null}
          showStatus={settings.showStatus}
          sortBy={settings.sortBy}
          hints={settings.hints}
          events={game.events}
        />
        {settings.showLog ? <Log lines={log} /> : null}
      </div>
      <Footer />
    </Shell>
  );
}

// --- online room ------------------------------------------------------------

function OnlineGame({ mode, setMode, settings, set }: ModeProps) {
  const t = useT();
  const net = useOnline(true);
  const [name, setName] = useState(() => net.savedName());
  const [code, setCode] = useState(() => roomCodeFromHash() ?? '');
  const autoJoinCode = useRef<string | null>(null);
  const hadRoom = useRef(false);
  const [dialog, setDialog] = useState<'close' | 'last-player' | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [clubMembers, setClubMembers] = useState<Array<{ id: string; displayName: string; active?: boolean }>>([]);
  const requestedRole = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('role') === 'spectator' ? 'spectator' as const : 'player' as const
    : 'player' as const;
  const requestedType = typeof window !== 'undefined' ? new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('type') : null;
  const requestedGame = requestedType === 'pong' || requestedType === 'codenames' ? requestedType : 'durak' as const;
  const isPong = requestedGame === 'pong' || net.room?.gameType === 'pong';
  const isCodenames = requestedGame === 'codenames' || net.room?.gameType === 'codenames';

  useEffect(() => {
    if (name.trim()) return;
    void fetch('/api/family/me').then((response) => response.ok ? response.json() as Promise<{ displayName?: string }> : null).then((profile) => {
      if (profile?.displayName) setName(profile.displayName);
    });
  }, [name]);

  useEffect(() => {
    const inviteCode = roomCodeFromHash();
    // Family members arrive here from the club with their profile name loaded
    // asynchronously; requiring a pre-existing localStorage name made the
    // join action silently fail on a fresh browser context.
    if (inviteCode === null || name.trim().length === 0) return;
    if (net.connection !== 'online' || net.room !== null) return;
    if (autoJoinCode.current === inviteCode) return;
    autoJoinCode.current = inviteCode;
    net.joinRoom(inviteCode, name.trim(), requestedRole);
  }, [net.connection, net.room, name]);

  useEffect(() => {
    if (net.room !== null) {
      hadRoom.current = true;
      return;
    }
    // A server-close is received while this route is still mounted. Returning
    // via the hash preserves the app-level LiveKit provider.
    if (hadRoom.current) {
      hadRoom.current = false;
      window.location.hash = '#/family';
    }
  }, [net.room]);

  useEffect(() => {
    if (!inviteOpen) return;
    void fetch('/api/family/members').then((response) => response.ok ? response.json() as Promise<Array<{ id: string; displayName: string; active?: boolean }>> : []).then(setClubMembers);
  }, [inviteOpen]);

  const isLastPlayer = net.room !== null
    && net.mySeat !== null
    && net.room.seats.filter((seat) => seat.kind === 'human').length === 1;
  const leave = () => {
    if (isLastPlayer) setDialog('last-player');
    else net.leaveRoom();
  };

  const seatName = (seat: number): string => {
    const occupant = net.room?.seats[seat];
    if (occupant === undefined || occupant.kind === 'empty') return t('player.seat', { n: seat + 1 });
    return occupant.name;
  };

  const model = useMemo(
    () =>
      net.view === null
        ? null
        : modelFromView(net.view, {
            seatName,
            isBot: (seat) => net.room?.seats[seat]?.kind === 'bot',
            connected: (seat) => {
              const occupant = net.room?.seats[seat];
              return occupant?.kind === 'human' ? occupant.connected : true;
            },
            substituted: (seat) => {
              const occupant = net.room?.seats[seat];
              return occupant?.kind === 'human' && occupant.substituted;
            },
          }),
    // `seatName` closes over the room, so the room belongs in the deps.
    [net.view, net.room],
  );

  const log = useMemo(() => {
    const lines: string[] = [];
    for (const e of net.events) {
      const line = describePublicEvent(e, t, seatName);
      if (line !== null) lines.push(line);
    }
    return lines.slice(-14).reverse();
  }, [net.events, net.room]);


  const inGame = net.room !== null && net.room.phase === 'playing' && model !== null;

  const menus: MenuSpec[] = [
    ...(isPong ? [] : [{
      id: 'settings',
      label: t('settings.title'),
      body: <SettingsPanel settings={settings} set={set} />,
    }]),
    ...(net.room !== null
      ? [
          {
            id: 'chat',
            label: t('lobby.chat'),
            badge: net.chat.length,
            alignRight: true,
            body: <Chat lines={net.chat} onSend={net.sendChat} />,
          },
        ]
      : []),
  ];

  const tone = net.connection === 'online' ? 'ok' : net.connection;

  return (
    <Shell
      mode={mode}
      setMode={setMode}
      settings={settings}
      set={set}
      menus={menus}
      onBack={() => { window.location.hash = '#/family'; }}
      {...(isPong ? { brandTitle: 'Family · Pong', showModeSwitch: false } : isCodenames ? { brandTitle: 'Family · Кодовые слова', showModeSwitch: false } : {})}
      {...(net.room !== null ? { onLeave: leave } : {})}
      {...(net.room !== null && net.room.hostId === net.playerId ? { onCloseRoom: () => setDialog('close') } : {})}
      status={
        <StatusChip tone={tone}>
          {net.room === null ? t(`conn.${net.connection}`) : `${t('lobby.room')} ${net.room.code}`}
        </StatusChip>
      }
    >
      <VoicePanel />
      {dialog === 'close' ? <RoomDialog title="Закрыть комнату?" text="Все игроки и наблюдатели вернутся в семейный клуб." dataTestId="close-room-dialog" onCancel={() => setDialog(null)} onConfirm={() => { setDialog(null); net.closeRoom(); }} confirm="Закрыть комнату" /> : null}
      {dialog === 'last-player' ? <LastPlayerDialog onCancel={() => setDialog(null)} onClose={() => { setDialog(null); net.leaveRoom('close'); }} onKeepOpen={() => { setDialog(null); net.leaveRoom('open'); }} /> : null}
      {net.error !== null ? (
        <p className="problem problem--error sheet" role="alert" onClick={net.clearError}>
          {explainError(net.error, t)}
        </p>
      ) : null}
      {inviteOpen && net.room ? <section className="room-dialog__scrim" role="presentation"><div className="sheet room-dialog" role="dialog" aria-modal="true"><h2>Пригласить в комнату</h2><p>Приглашение увидят только участники, которые сейчас в клубе.</p>{clubMembers.filter((person) => person.active !== false).map((person) => <button className="room-invite-person" key={person.id} onClick={() => { net.inviteToRoom(person.id); setInviteOpen(false); }}>{person.displayName}</button>)}<div className="actions"><button onClick={() => setInviteOpen(false)}>Готово</button></div></div></section> : null}

      {net.room === null && isPong ? (
        <PongEntry name={name} setName={setName} canAct={net.connection === 'online'} onCreate={() => net.createRoom(name.trim(), DEFAULT_RULES, 'pong')} />
      ) : null}

      {net.room === null && isCodenames ? (
        <CodenamesEntry name={name} setName={setName} canAct={net.connection === 'online'} onCreate={() => net.createRoom(name.trim(), DEFAULT_RULES, 'codenames')} />
      ) : null}

      {net.room === null && !isPong && !isCodenames ? (
        <Entry
          name={name}
          setName={setName}
          code={code}
          setCode={setCode}
          canAct={net.connection === 'online'}
          onCreate={() => net.createRoom(name.trim(), DEFAULT_RULES, requestedGame)}
          onJoin={() => net.joinRoom(code.trim(), name.trim())}
          onWatch={() => net.joinRoom(code.trim(), name.trim(), 'spectator')}
        />
      ) : null}

      {net.room !== null && !inGame && net.room.gameType !== 'pong' && net.room.gameType !== 'codenames' ? (
        <Lobby
          room={net.room}
          mySeat={net.mySeat}
          playerId={net.playerId ?? ''}
          onConfig={net.setConfig}
          onSeat={net.takeSeat}
          onReleaseSeat={net.releaseSeat}
          onAddBot={net.addBot}
          onRemoveBot={net.removeBot}
          onStart={net.room.phase === 'finished' ? net.rematch : net.start}
        />
      ) : null}

      {inGame && model !== null && net.room ? (
        <GameRoomShell room={net.room} onInvite={() => setInviteOpen(true)}><div className="stage">
          {model.result ? <ResultBanner result={model.result} seatName={seatName} /> : null}
          <Board
            model={model}
            showStatus={settings.showStatus}
            sortBy={settings.sortBy}
            hints={settings.hints}
            events={net.events}
            play={(move) => {
              if (net.view) net.move(net.view.seq, move);
            }}
          />
          {settings.showLog ? <Log lines={log} /> : null}
        </div></GameRoomShell>
      ) : null}
      {net.room?.gameType === 'pong' ? <GameRoomShell room={net.room} onInvite={() => setInviteOpen(true)}><PongRoom room={net.room} state={net.pong} mySeat={net.mySeat} playerId={net.playerId} onInput={(direction) => net.pongInput(direction, net.pong?.step ?? 0)} onStart={net.pongStart} onRematch={net.pongRematch} onExit={leave} /></GameRoomShell> : null}
      {net.room?.gameType === 'codenames' ? <GameRoomShell room={net.room} onInvite={() => setInviteOpen(true)}><CodenamesRoom room={net.room} state={net.codenames} playerId={net.playerId} onStart={net.codenamesStart} onClue={(word, count) => net.codenames && net.codenamesClue(net.codenames.step, word, count)} onChoose={(index) => net.codenames && net.codenamesChoose(net.codenames.step, index)} onEnd={() => net.codenames && net.codenamesEnd(net.codenames.step)} onRematch={net.codenamesRematch} /></GameRoomShell> : null}
      {!isPong && !isCodenames ? <Footer /> : null}
    </Shell>
  );
}

function RoomDialog({ title, text, dataTestId, confirm, onCancel, onConfirm }: { title: string; text: string; dataTestId: string; confirm: string; onCancel: () => void; onConfirm: () => void }) {
  return <div className="room-dialog__scrim" role="presentation"><section className="sheet room-dialog" data-testid={dataTestId} role="dialog" aria-modal="true"><h2>{title}</h2><p>{text}</p><div className="actions"><button data-testid="cancel-room-dialog" type="button" onClick={onCancel}>Отмена</button><button data-testid="confirm-close-room" type="button" className="btn btn--primary" onClick={onConfirm}>{confirm}</button></div></section></div>;
}

function LastPlayerDialog({ onCancel, onClose, onKeepOpen }: { onCancel: () => void; onClose: () => void; onKeepOpen: () => void }) {
  return <div className="room-dialog__scrim" role="presentation"><section className="sheet room-dialog" data-testid="last-player-dialog" role="dialog" aria-modal="true"><h2>Вы последний игрок в комнате</h2><p>Что сделать с комнатой?</p><div className="actions"><button data-testid="last-player-cancel" type="button" onClick={onCancel}>Отмена</button><button data-testid="last-player-keep-open" type="button" onClick={onKeepOpen}>Оставить комнату открытой</button><button data-testid="last-player-close" type="button" className="btn btn--primary" onClick={onClose}>Закрыть комнату</button></div></section></div>;
}

function PongEntry({ name, setName, canAct, onCreate }: { name: string; setName: (value: string) => void; canAct: boolean; onCreate: () => void }) {
  return <section className="pong-entry" data-testid="pong-entry">
    <div className="pong-entry__glow" />
    <div className="pong-entry__copy"><span className="pong-kicker">PONG · 2 ИГРОКА</span><h1>Быстрый матч для двоих</h1><p>Создайте комнату, дождитесь второго игрока и начинайте. Наблюдатели могут смотреть матч в реальном времени.</p><div className="pong-entry__rules"><span>До 11 очков</span><span>Победа в 2 очка</span><span>W/S или ↑/↓</span></div></div>
    <div className="pong-entry__form"><label className="field"><span>Ваше имя</span><input value={name} maxLength={20} placeholder="Как вас показать в матче" onChange={(event) => setName(event.target.value)} /></label><button data-testid="create-room" type="button" className="pong-cta" disabled={!canAct || name.trim().length === 0} onClick={onCreate}>Создать комнату Pong <span>→</span></button><small>После создания отправьте ссылку или дождитесь участника из семейного клуба.</small></div>
  </section>;
}

function PongRoom({ room, state, mySeat, playerId, onInput, onStart, onRematch, onExit }: { room: RoomState; state: import('../pong/engine.js').PongState | null; mySeat: number | null; playerId: string | null; onInput: (direction: -1 | 0 | 1) => void; onStart: () => void; onRematch: () => void; onExit: () => void }) {
  const players = room.seats.filter((seat): seat is Extract<RoomState['seats'][number], { kind: 'human' }> => seat.kind === 'human');
  const leftName = players[0]?.name ?? 'Игрок слева';
  const rightName = players[1]?.name ?? 'Ожидание игрока';
  const side = mySeat === 0 || mySeat === 1 ? mySeat : null;
  const isHost = room.hostId === playerId;
  if (!state) return <section className="pong-waiting"><span className="pong-kicker">КОМНАТА PONG · {room.code}</span><h1>{players.length < 2 ? 'Ожидаем второго игрока' : 'Все готовы к матчу'}</h1><p>{players.length < 2 ? 'Пригласите второго игрока из семейного клуба. Когда он войдёт игроком, матч можно будет начать.' : 'Оба игрока за столом. Создатель комнаты начинает матч.'}</p><div className="pong-roster"><span><i />{leftName} · слева</span><span><i className={players[1] ? 'pong-roster__online' : ''} />{rightName} · справа</span></div>{isHost && players.length === 2 ? <button data-testid="pong-start" className="pong-cta" onClick={onStart}>Начать матч <span>→</span></button> : null}</section>;
  return <section className="pong-stage"><PongBoard state={state} side={side} leftName={leftName} rightName={rightName} onInput={onInput} />{state.phase === 'finished' ? <div className="pong-result-actions"><div><strong>{side === null ? 'Матч завершён' : 'Готовы к следующему матчу?'}</strong><span>{side === null ? 'Спасибо за игру.' : 'Реванш начнётся, когда оба игрока подтвердят участие.'}</span></div><div>{side !== null ? <button data-testid="pong-rematch" className="pong-cta" onClick={onRematch}>Сыграть ещё <span>↻</span></button> : null}<button data-testid="pong-exit" type="button" className="pong-exit" onClick={onExit}>Выйти в семейный клуб</button></div></div> : null}</section>;
}

/**
 * The way in: a name, and either a new room or somebody else's code.
 *
 * No account, no password: inventing a login for a room that lives in server
 * memory would be theatre, so the way in is the two things the game actually
 * needs — who you are at the table, and which table.
 */
function Entry({
  name,
  setName,
  code,
  setCode,
  canAct,
  onCreate,
  onJoin,
  onWatch,
}: {
  name: string;
  setName: (value: string) => void;
  code: string;
  setCode: (value: string) => void;
  canAct: boolean;
  onCreate: () => void;
  onJoin: () => void;
  onWatch: () => void;
}) {
  const t = useT();
  return (
    <div className="sheet entry">
      <div>
        <div className="kicker">{t('entry.kicker')}</div>
        <h1>{t('entry.title')}</h1>
        <p className="entry__lede">{t('mode.onlineLede')}</p>

        <div className="entry__form">
          <label className="field">
            <span>{t('field.name')}</span>
            <input value={name} maxLength={20} onChange={(e) => setName(e.target.value)} />
          </label>
          <button data-testid="create-room"
            type="button"
            className="btn btn--primary btn--block"
            disabled={!canAct || name.trim().length === 0}
            onClick={onCreate}
          >
            {t('action.createRoom')}
          </button>
          <button
            type="button"
            className="btn btn--block"
            disabled={!canAct || code.trim().length !== 5}
            onClick={onWatch}
          >
            {t('action.watch')}
          </button>

          <label className="field" data-testid="room-code-field">
            <span>{t('field.roomCode')}</span>
            <input
              value={code}
              maxLength={5}
              placeholder="ABC12"
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          </label>
          <button
            type="button"
            className="btn btn--block"
            disabled={!canAct || code.trim().length !== 5}
            onClick={onJoin}
          >
            {t('action.join')}
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="kicker">{t('entry.botsKicker')}</div>
        <h3>{t('entry.botsTitle')}</h3>
        <ul className="entry__bots">
          {BOT_CATALOGUE.filter((b) => b.available).map((bot) => (
            <li key={bot.level}>
              <strong>{t(bot.nameKey)}</strong>
              <span>{t(bot.blurbKey)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// --- shared bits ------------------------------------------------------------

function ResultBanner({
  result,
  seatName,
}: {
  result: GameResult;
  seatName: (seat: number) => string;
}) {
  const t = useT();
  const text =
    result.reason === 'stalemate'
      ? t('banner.stalemate')
      : result.loserTeam !== null
        ? // With teams the loss belongs to the side, not to one player.
          t('banner.teamLost', { team: t('team.name', { n: result.loserTeam + 1 }) })
        : result.durak === null
          ? t('banner.draw')
          : // Player ids are seat-derived ("p0" locally, "s0" on the server).
            t('banner.durak', { name: seatName(Number(result.durak.replace(/^\D+/, ''))) });
  return <section className={`banner${result.durak === null ? '' : ' banner--loss'}`}>{text}</section>;
}

function Log({ lines }: { lines: string[] }) {
  const t = useT();
  return (
    <section className="log">
      <h2>{t('log.title')}</h2>
      <ol>
        {lines.map((line, i) => (
          <li key={`${i}-${line}`}>{line}</li>
        ))}
      </ol>
    </section>
  );
}

function Footer() {
  const { t } = useI18n();
  return (
    <footer className="footer">
      <span>v{APP_VERSION}</span>
      <span>
        <a href="https://tekeye.uk" rel="noreferrer noopener" target="_blank">
          {t('app.cardsCredit')}
        </a>
      </span>
    </footer>
  );
}

function describeBots(bots: readonly (BotLevel | null)[], t: Translate): string {
  const levels = bots.filter((b): b is BotLevel => b !== null);
  if (levels.length === 0) return t('bots.hint.none');
  return [...new Set(levels)]
    .map((l) => BOT_CATALOGUE.find((b) => b.level === l))
    .filter((b) => b !== undefined)
    .map((b) => `${t(b.nameKey)} — ${t(b.blurbKey)}`)
    .join(' ');
}

/** Server error codes and rule warnings share one lookup: code in, sentence out. */
const explainError = (code: string, t: Translate): string => {
  const message = t(`error.${code}`);
  return message === `error.${code}` ? t('error.unknown', { code }) : message;
};

const explain = (problem: ConfigProblem, t: Translate): string =>
  t(`problem.${problem.code}`, problem.params ?? {});

export default App;
